import Link from 'next/link';
import { withRouter } from 'next/router';
import { Fragment, useEffect, useRef, useState } from 'react';

import { getStringsAsKebabCase, isCurrentlyActive } from '@lib/utils';
import type { NavBarType } from '@lib/constants';
import Icon from '@components/atoms/icon';

const MAXIMUM_CHIP_COUNT = 7;
const DESKTOP_BREAKPOINT_QUERY = '(min-width: 768px)';
const ALL_TOOLS_ID = 'sub-menu-all-tools';

const labelClasses =
  'font-sans text-[11px] uppercase tracking-[0.1em] text-neutral-500';
const groupHeadingClasses = `${labelClasses} font-bold`;

type SubMenuGroup = {
  heading: string | null;
  items: NavBarType[];
};

const buildGroups = (items: NavBarType[]) => {
  const groups: SubMenuGroup[] = [];

  items.forEach((item) => {
    if (item.type === 'divider') {
      groups.push({ heading: item.name, items: [] });
      return;
    }
    if (!item.url) return;
    if (groups.length === 0) {
      groups.push({ heading: null, items: [] });
    }
    groups[groups.length - 1].items.push(item);
  });

  return groups.filter((group) => group.items.length > 0);
};

// Chips keep their configured order so tools stay where users expect them;
// the active item is only hoisted when it would otherwise be hidden behind
// the "All tools" trigger
const buildChipItems = (items, activeItem) => {
  const shouldHoistActiveItem =
    activeItem && items.indexOf(activeItem) >= MAXIMUM_CHIP_COUNT;

  const orderedItems = shouldHoistActiveItem
    ? [activeItem, ...items.filter((item) => item !== activeItem)]
    : items;

  return orderedItems.slice(0, MAXIMUM_CHIP_COUNT);
};

const SubMenu = ({ router, items, label }) => {
  const [isAllToolsOpen, setIsAllToolsOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const railRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const path = router.asPath.split('?')[0];

  const linkItems = items.filter((item) => Boolean(item?.url));
  const activeItem = linkItems.find((item) => isCurrentlyActive(path, item));

  const chipItems = buildChipItems(linkItems, activeItem);

  const groups = buildGroups(items);

  const closeAllTools = ({ shouldRestoreFocus = true } = {}) => {
    setIsAllToolsOpen(false);
    if (shouldRestoreFocus) {
      triggerRef.current?.focus();
    }
  };

  useEffect(() => {
    const handleRouteChange = () => setIsAllToolsOpen(false);
    router.events.on('routeChangeStart', handleRouteChange);
    return () => router.events.off('routeChangeStart', handleRouteChange);
  }, [router.events]);

  useEffect(() => {
    const railElement = railRef.current;
    if (!railElement) return;

    const activeChip = railElement.querySelector<HTMLElement>(
      '[aria-current="page"]'
    );
    if (activeChip) {
      activeChip.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    } else {
      railElement.scrollLeft = 0;
    }
  }, [path]);

  useEffect(() => {
    if (!isAllToolsOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsAllToolsOpen(false);
        triggerRef.current?.focus();
      }
    };
    const handleClickOutside = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setIsAllToolsOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isAllToolsOpen]);

  useEffect(() => {
    if (!isAllToolsOpen) return undefined;

    const isDesktop = window.matchMedia(DESKTOP_BREAKPOINT_QUERY).matches;
    if (isDesktop) return undefined;

    closeButtonRef.current?.focus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isAllToolsOpen]);

  const handleSheetKeyDown = (event) => {
    if (event.key !== 'Tab') return;

    const focusableElements =
      sheetRef.current?.querySelectorAll<HTMLElement>('a[href], button');
    if (!focusableElements?.length) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  };

  const renderChip = (item) => {
    const isActive = isCurrentlyActive(path, item);

    return (
      <Link
        key={item.level}
        href={item.url}
        target={item.isOpenInNewWindow ? '_blank' : '_self'}
        aria-current={isActive ? 'page' : undefined}
        className={`inline-flex h-9 shrink-0 items-center whitespace-nowrap rounded-lg px-3.5 font-open-sans text-[15px] ${
          isActive
            ? 'bg-[#F1F1F3] font-semibold text-neutral-800'
            : 'text-neutral-700 hover:bg-neutral-100 hover:text-neutral-800'
        }`}
        data-cy={`submenu-item-${getStringsAsKebabCase(item.name)}`}
      >
        {item.name}
        {item.isOpenInNewWindow && (
          <Icon name="ExternalLink" className="ml-1 w-3 h-3" />
        )}
      </Link>
    );
  };

  const renderPanelLink = (item) => {
    const isActive = isCurrentlyActive(path, item);

    return (
      <Link
        key={item.level}
        href={item.url}
        target={item.isOpenInNewWindow ? '_blank' : '_self'}
        aria-current={isActive ? 'page' : undefined}
        className={`flex items-center py-[9px] font-open-sans text-[15px] text-neutral-800 ${
          isActive ? 'justify-between font-semibold' : 'hover:text-primary-600'
        }`}
        data-cy={`submenu-item-${getStringsAsKebabCase(item.name)}`}
      >
        {/* the global `* { font-weight }` reset stops the link's weight
            reaching this span, so the active weight is repeated here */}
        <span
          className={`flex items-center ${isActive ? 'font-semibold' : ''}`}
        >
          {item.name}
          {item.isOpenInNewWindow && (
            <Icon name="ExternalLink" className="ml-1 w-3 h-3" />
          )}
        </span>
        {isActive && (
          <span className="h-2 w-2 rounded-full bg-primary-600" aria-hidden />
        )}
      </Link>
    );
  };

  const renderSheetRow = (item) => {
    const isActive = isCurrentlyActive(path, item);

    return (
      <Link
        key={item.level}
        href={item.url}
        target={item.isOpenInNewWindow ? '_blank' : '_self'}
        aria-current={isActive ? 'page' : undefined}
        className={`flex items-center justify-between px-4 py-[13px] font-open-sans text-base text-neutral-800 ${
          isActive ? 'bg-neutral-100 font-semibold' : 'hover:bg-neutral-100'
        }`}
        data-cy={`submenu-item-${getStringsAsKebabCase(item.name)}`}
      >
        {/* the global `* { font-weight }` reset stops the link's weight
            reaching this span, so the active weight is repeated here */}
        <span
          className={`flex items-center ${isActive ? 'font-semibold' : ''}`}
        >
          {item.name}
          {item.isOpenInNewWindow && (
            <Icon name="ExternalLink" className="ml-1 w-3 h-3" />
          )}
        </span>
        {isActive && (
          <span className="h-2 w-2 rounded-full bg-primary-600" aria-hidden />
        )}
      </Link>
    );
  };

  return (
    <div ref={rootRef}>
      <div className="bg-white border-b border-neutral-200">
        <div className="xl:container max-w-screen-3xl mx-auto flex items-center md:h-16 md:gap-5 md:px-4">
          {label && (
            <>
              <span
                className={`hidden md:block shrink-0 whitespace-nowrap font-semibold ${labelClasses}`}
              >
                {label}
              </span>
              <span
                className="hidden md:block h-6 w-px shrink-0 bg-neutral-200"
                aria-hidden
              />
            </>
          )}
          <div
            ref={railRef}
            className="scrollbar-hide flex min-w-0 flex-1 items-center gap-1 overflow-x-auto scroll-smooth py-2.5 pl-3.5 pr-4 sm:pl-8 md:p-0"
          >
            {chipItems.map(renderChip)}
          </div>
          <div className="relative shrink-0 py-2.5 pl-2 pr-3 sm:pr-8 -ml-4 bg-[linear-gradient(to_right,rgba(255,255,255,0),#fff_14px)] md:ml-0 md:bg-none md:p-0">
            <button
              ref={triggerRef}
              type="button"
              aria-expanded={isAllToolsOpen}
              aria-controls={ALL_TOOLS_ID}
              onClick={() => setIsAllToolsOpen(!isAllToolsOpen)}
              className="inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-lg border border-neutral-300 bg-white px-3 font-open-sans text-[15px] font-semibold text-neutral-800 hover:border-neutral-800"
              data-cy="submenu-all-tools"
            >
              {/* one flex item so the flex gap doesn't split the label, with
                  the weight repeated on the spans to survive the global
                  `* { font-weight }` reset */}
              <span className="font-semibold">
                All
                <span className="hidden font-semibold md:inline">
                  &nbsp;tools
                </span>
              </span>
              <Icon
                name="ChevronDown"
                className={`h-3.5 w-3.5 text-neutral-600 transition-transform duration-150 motion-reduce:transition-none ${
                  isAllToolsOpen ? 'rotate-180' : ''
                }`}
              />
            </button>
          </div>
        </div>
      </div>
      {isAllToolsOpen && (
        <div id={ALL_TOOLS_ID}>
          <div className="hidden md:block animate-sub-menu-fade-in motion-reduce:animate-none bg-neutral-100 border-b border-neutral-200">
            <div className="xl:container max-w-screen-3xl mx-auto grid grid-cols-4 gap-x-10 gap-y-1 px-4 pt-6 pb-7">
              {groups.map((group, groupIndex) => (
                <Fragment key={group.heading ?? groupIndex}>
                  {groupIndex > 0 && (
                    <div
                      className="col-span-full mt-3.5 mb-1.5 h-px bg-neutral-200"
                      aria-hidden
                    />
                  )}
                  {group.heading && (
                    <div
                      className={`col-span-full pb-1.5 ${groupHeadingClasses}`}
                    >
                      {group.heading}
                    </div>
                  )}
                  {group.items.map(renderPanelLink)}
                </Fragment>
              ))}
            </div>
          </div>
          <div className="md:hidden fixed inset-0 z-[60]">
            <div
              className="animate-sub-menu-fade-in motion-reduce:animate-none absolute inset-0 bg-neutral-800/55"
              onClick={() => closeAllTools()}
              aria-hidden
            />
            <div
              ref={sheetRef}
              role="dialog"
              aria-label="All tools"
              onKeyDown={handleSheetKeyDown}
              className="animate-sub-menu-slide-in motion-reduce:animate-none absolute inset-x-0 bottom-0 max-h-[82%] overflow-y-auto rounded-t-2xl bg-white pt-2.5 pb-5 shadow-[0px_-2px_25px_rgba(0,0,0,0.15)]"
            >
              <div
                className="mx-auto mb-3.5 h-1 w-10 rounded-full bg-neutral-300"
                aria-hidden
              />
              <div className="flex items-center justify-between px-4 pb-2">
                <span className="font-sans text-[17px] font-semibold text-neutral-800">
                  All tools
                </span>
                <button
                  ref={closeButtonRef}
                  type="button"
                  aria-label="Close"
                  onClick={() => closeAllTools()}
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-[#F4F4F6]"
                  data-cy="submenu-all-tools-close"
                >
                  <Icon
                    name="Close"
                    className="h-[15px] w-[15px] text-neutral-700"
                  />
                </button>
              </div>
              {groups.map((group, groupIndex) => (
                <Fragment key={group.heading ?? groupIndex}>
                  {group.heading && (
                    <div
                      className={`px-4 pb-1.5 ${groupHeadingClasses} ${
                        groupIndex > 0
                          ? 'mt-2 border-t border-neutral-200 pt-[18px]'
                          : 'pt-3'
                      }`}
                    >
                      {group.heading}
                    </div>
                  )}
                  {group.items.map(renderSheetRow)}
                </Fragment>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default withRouter(SubMenu);
